import React, { useState, useEffect, useRef, useCallback } from 'react';
import { loadGoogleMaps } from '../utils/loadGoogleMaps';

// Hidden div for PlacesService (requires a DOM node)
let placesDiv = null;
function getPlacesDiv() {
  if (!placesDiv) {
    placesDiv = document.createElement('div');
    document.body.appendChild(placesDiv);
  }
  return placesDiv;
}

// ⚠️ `style` WAS ADDED FOR DOORLYPRO, WHICH USES INLINE STYLES RATHER THAN
// TAILWIND CLASSES. The borrower application passes className; the partner form
// has no utility classes to pass, and a second autocomplete component would be a
// second Places integration to keep working.
function AddressAutocomplete({ value, name, onChange, onSelect, className, style,
  placeholder, autoComplete }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [apiReady, setApiReady] = useState(false);
  const autocompleteRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const debounceRef = useRef(null);

  // Load Google Maps once
  useEffect(() => {
    loadGoogleMaps()
      .then(() => {
        console.log('[AddressAutocomplete] Initializing AutocompleteService for field:', name);
        autocompleteRef.current = new window.google.maps.places.AutocompleteService();
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
        setApiReady(true);
        console.log('[AddressAutocomplete] Ready — field:', name);
      })
      .catch((err) => {
        console.error('[AddressAutocomplete] Google Maps load failed for field:', name, '|', err.message);
      });
  }, [name]);

  const fetchSuggestions = useCallback((input) => {
    if (!apiReady || !autocompleteRef.current || input.length < 3) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }
    autocompleteRef.current.getPlacePredictions(
      {
        input,
        sessionToken: sessionTokenRef.current,
        types: ['address'],
        componentRestrictions: { country: 'us' },
      },
      (predictions, status) => {
        console.log('[AddressAutocomplete] Predictions status:', status, '| count:', predictions?.length ?? 0);
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          predictions?.length
        ) {
          setSuggestions(predictions);
          setShowDropdown(true);
        } else {
          setSuggestions([]);
          setShowDropdown(false);
        }
      }
    );
  }, [apiReady]);

  const handleChange = (e) => {
    onChange(e);
    const val = e.target.value;
    setActiveIdx(-1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 250);
  };

  const selectSuggestion = (prediction) => {
    const placesService = new window.google.maps.places.PlacesService(getPlacesDiv());
    placesService.getDetails(
      {
        placeId: prediction.place_id,
        fields: ['address_components'],
        sessionToken: sessionTokenRef.current,
      },
      (place, status) => {
        // Reset session token immediately after use (per Google billing guidance)
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();

        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) return;

        const get = (types) => {
          const comp = place.address_components.find((c) =>
            types.some((t) => c.types.includes(t))
          );
          return comp ? comp.long_name : '';
        };
        const getShort = (types) => {
          const comp = place.address_components.find((c) =>
            types.some((t) => c.types.includes(t))
          );
          return comp ? comp.short_name : '';
        };

        const streetNumber = get(['street_number']);
        const route = get(['route']);
        const street = [streetNumber, route].filter(Boolean).join(' ');
        const city = get(['locality', 'sublocality_level_1']);
        const state = getShort(['administrative_area_level_1']);
        const zip = get(['postal_code']);

        onSelect({ street, city, state, zip });
      }
    );

    setSuggestions([]);
    setShowDropdown(false);
    setActiveIdx(-1);
  };

  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      selectSuggestion(suggestions[activeIdx]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      setActiveIdx(-1);
    }
  };

  return (
    <div className="relative">
      {/* Location pin icon */}
      <svg
        className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
        style={{ color: '#3a6ad9', opacity: 0.7 }}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>

      <input
        type="text"
        name={name}
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete || 'off'}
        className={`${className || ''} pl-9`}
        // 🚨 paddingLeft LAST, AND THAT IS THE WHOLE FIX. The first version read
        // `{ paddingLeft: 34, ...style, …paddingLeft again }` — and because a
        // repeated key keeps its FIRST position in the object, the emitted CSS
        // was `padding-left: 34px; padding: 10px 12px`, so the shorthand that
        // arrived in `style` reset the left padding and the location pin sat on
        // top of the placeholder text. Spreading the caller's style first and
        // setting paddingLeft after it is the only order that works.
        //
        // 🔑 THERE IS A PIN ICON IN THAT SPACE, so the room is not negotiable and
        // is reserved here rather than left to every caller to remember.
        style={style ? { ...style, paddingLeft: 34 } : undefined}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={() => setTimeout(() => { setShowDropdown(false); setActiveIdx(-1); }, 160)}
        onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
      />

      {showDropdown && suggestions.length > 0 && (
        <ul
          className="absolute z-50 w-full mt-1 bg-white rounded-xl shadow-xl overflow-hidden"
          style={{ border: '1px solid #e5e7eb' }}
        >
          {suggestions.map((pred, idx) => (
            <li
              key={pred.place_id}
              onMouseDown={() => selectSuggestion(pred)}
              className="px-4 py-3 text-sm cursor-pointer flex items-start gap-2.5 transition-colors duration-100"
              style={{
                backgroundColor: idx === activeIdx ? '#3a6ad9' : 'white',
                color: idx === activeIdx ? 'white' : '#111827',
              }}
              onMouseEnter={() => setActiveIdx(idx)}
              onMouseLeave={() => setActiveIdx(-1)}
            >
              <svg
                className="w-3.5 h-3.5 mt-0.5 flex-shrink-0"
                style={{ opacity: 0.55 }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="leading-snug">{pred.description}</span>
            </li>
          ))}
          {/* Google attribution — required by ToS */}
          <li className="px-4 py-2 flex justify-end pointer-events-none">
            <img
              src="https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3.png"
              alt="Powered by Google"
              style={{ height: '14px' }}
            />
          </li>
        </ul>
      )}
    </div>
  );
}

export default AddressAutocomplete;
